/**
 * F-receipts-100pct + xlsx upload — Converte arquivo .xlsx em string CSV
 * para reuso no fluxo de import eDSP. Browser-side via SheetJS.
 *
 * Backend continua recebendo CSV string (sem mudanças nos parsers existentes).
 * Frontend detecta extensão `.xlsx` na seleção do file picker e converte
 * antes de enviar via tRPC.
 *
 * Convenções:
 *   - 1ª sheet do workbook (eDSP exporta sempre só uma)
 *   - Separador: vírgula (mesmo formato dos CSVs eDSP atuais)
 *   - Aspas em volta de todos os fields (mais defensivo p/ vírgulas em valores)
 *   - Datas serializadas como string (não Date object)
 */

import * as XLSX from "xlsx";

/**
 * Lê um File (xlsx) e retorna o conteúdo da 1ª sheet como CSV string.
 * Equivalente ao que `readAsText` faria com um CSV. Pode ser usado
 * intercambiavelmente nas mesmas procedures de import.
 */
export async function xlsxFileToCsv(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, {
    type: "array",
    cellDates: false, // mantém string FR como "lundi 4 août 2025"
    raw: false, // força formatação visível (não o valor cru)
  });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error("Arquivo XLSX vazio ou sem planilhas.");
  }
  const sheet = workbook.Sheets[firstSheetName]!;
  return XLSX.utils.sheet_to_csv(sheet, {
    FS: ",",
    forceQuotes: true,
    blankrows: false,
  });
}

/**
 * Lê um File (xlsx/xls/csv) e devolve as linhas da 1ª sheet como objetos
 * keyed pelo cabeçalho (linha 1). Usado pelo import do Parc Grêle.
 */
export async function readSheetRows(file: File): Promise<Record<string, string>[]> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false, raw: false });
  const first = workbook.SheetNames[0];
  if (!first) throw new Error("Arquivo vazio ou sem planilhas.");
  const sheet = workbook.Sheets[first]!;
  return XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false }) as Record<string, string>[];
}

/**
 * Lê um arquivo (CSV ou XLSX) e retorna string CSV. Conveniência:
 * componente UI passa o File direto sem precisar testar extensão.
 */
export async function readImportFileAsCsv(file: File): Promise<string> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    return xlsxFileToCsv(file);
  }
  // CSV ou .txt — lê como texto
  return await file.text();
}
